import React, { useState } from "react";
import {
  Card,
  Row,
  Col,
  Badge,
  Tooltip,
  Tag,
  Button,
  Form,
  Input,
  Select,
  Switch,
  DatePicker,
  Popover,
  Divider,
  Space,
  message,
} from "antd";
import Draggable from "react-draggable";
import {
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  TagOutlined,
  FileTextOutlined,
  EnvironmentOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import "./MonthView.css";
import { Event, CalendarCategory } from "./activityCalendar";
import api from "../../../services/api";
import { showBasicAlert } from "../../../core/common/useAlert";

const { TextArea } = Input;
const { Option } = Select;

interface MonthViewProps {
  currentDate: Dayjs;
  events: Event[];
  categories: CalendarCategory[];
  onEventClick: (event: Event) => void;
  onDateClick: (date: Dayjs) => void;
  onEventsChange: (events: Event[]) => void;
  onEventSaved?: () => void;
  selectedCompany: { id: string; type: string } | null;
}

const MonthView: React.FC<MonthViewProps> = ({
  currentDate,
  events,
  categories,
  onEventClick,
  onDateClick,
  onEventsChange,
  onEventSaved,
}) => {
  const signature = localStorage.getItem("signature");
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isEventDetailVisible, setIsEventDetailVisible] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [formPosition, setFormPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [detailPosition, setDetailPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [activePopover, setActivePopover] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm();

  const startOfMonth = currentDate.startOf("month");
  const endOfMonth = currentDate.endOf("month");
  const startDate = startOfMonth.startOf("week");
  const endDate = endOfMonth.endOf("week");

  const days: Dayjs[] = [];
  let day = startDate;

  while (day.isBefore(endDate)) {
    days.push(day);
    day = day.add(1, "day");
  }

  const weeks: Dayjs[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  // Fixed: Properly filter events for each day
  const getEventsForDay = (day: Dayjs) => {
    return events.filter((event) => {
      const eventStart = event.start.startOf("day");
      const eventEnd = event.end.startOf("day");
      const currentDay = day.startOf("day");

      return (
        eventStart.isSame(currentDay, "day") ||
        eventEnd.isSame(currentDay, "day") ||
        (eventStart.isBefore(currentDay) && eventEnd.isAfter(currentDay)) ||
        (event.allDay && eventStart.isSame(currentDay, "day"))
      );
    });
  };

  // Get latest one events for a day (sorted by start time)
  const getLatestEventsForDay = (day: Dayjs) => {
    const dayEvents = getEventsForDay(day);
    return dayEvents
      .sort((a, b) => b.start.valueOf() - a.start.valueOf())
      .slice(0, 1);
  };

  // Get remaining events count
  const getRemainingEventsCount = (day: Dayjs) => {
    const dayEvents = getEventsForDay(day);
    return Math.max(0, dayEvents.length - 1);
  };

  // Handle event click - show detail card instead of modal
  const handleEventClick = (event: Event, clickEvent: React.MouseEvent) => {
    setSelectedEvent(event);
    setActivePopover(null); // Close any open popover

    const container = document.querySelector(
      ".modern-month-view"
    ) as HTMLElement;
    const containerRect = container.getBoundingClientRect();

    let x = clickEvent.clientX - containerRect.left;
    let y = clickEvent.clientY - containerRect.top;

    const cardWidth = 400;
    const cardHeight = 500;

    if (x + cardWidth > containerRect.width) {
      x = containerRect.width - cardWidth - 10;
    }
    if (y + cardHeight > containerRect.height) {
      y = containerRect.height - cardHeight - 10;
    }
    if (x < 0) x = 10;
    if (y < 0) y = 10;

    setDetailPosition({ x, y });
    setIsEventDetailVisible(true);
  };

  // Handle all day switch change
  const handleAllDayChange = (allDay: boolean) => {
    const start = form.getFieldValue("start");
    const end = form.getFieldValue("end");

    if (allDay) {
      if (start) form.setFieldValue("start", start.startOf("day"));
      if (end) form.setFieldValue("end", end.endOf("day"));
    } else {
      const now = dayjs();
      if (start)
        form.setFieldValue(
          "start",
          start.startOf("day").set("hour", 9).set("minute", 0)
        );
      if (end)
        form.setFieldValue(
          "end",
          end.startOf("day").set("hour", 17).set("minute", 0)
        );
    }
  };

  const handleAddEventClick = (day: Dayjs, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedDate(day);

    // Calculate position for the form card
    const rect = event.currentTarget.getBoundingClientRect();
    const container = document.querySelector(
      ".modern-month-view"
    ) as HTMLElement;
    const containerRect = container.getBoundingClientRect();

    let x = rect.left - containerRect.left + rect.width / 2;
    let y = rect.top - containerRect.top + rect.height;

    // Ensure the card stays inside the container
    const cardWidth = 400;
    const cardHeight = 500;

    if (x + cardWidth > containerRect.width) {
      x = containerRect.width - cardWidth - 20;
    }
    if (y + cardHeight > containerRect.height) {
      y = rect.top - containerRect.top - cardHeight;
    }
    if (x < 20) x = 20;
    if (y < 20) y = 20;

    setFormPosition({ x, y });
    setIsFormVisible(true);
    setIsEditing(false);
    setActivePopover(null); // Close any open popover

    // Set default values for the new event
    const defaultStart = day.set("hour", 9).set("minute", 0);
    const defaultEnd = day.set("hour", 17).set("minute", 0);

    form.resetFields();
    form.setFieldsValue({
      title: "",
      description: "",
      start: defaultStart,
      end: defaultEnd,
      allDay: false,
      category: categories[0]?.name || "Meeting",
      color: categories[0]?.color || "#1890ff",
      is_public: false,
    });
  };

  const handleSaveEvent = () => {
    form.validateFields().then(async (values) => {
      let { start, end, allDay } = values;

      // Ensure all-day events cover the full day
      if (allDay) {
        start = start.startOf("day");
        end = end.endOf("day");
      }

      const categoryId =
        categories.find((c) => c.name === values.category)?.id || "1";

      if (isEditing && selectedEvent) {
        // Update existing event
        const data = {
          signature,
          id: selectedEvent.id,
          title: values.title,
          description: values.description,
          start_date: start.format("YYYY-MM-DD HH:mm:ss"),
          end_date: end.format("YYYY-MM-DD HH:mm:ss"),
          is_all_day: allDay,
          event_type: parseInt(categoryId),
          is_public: values.is_public || false,
        };
        try {
          await api.post("activityCalendarService/updateEvent", data);
          onEventSaved?.();
          setIsFormVisible(false);
          setFormPosition(null);
          setIsEditing(false);
          setSelectedEvent(null);
          message.success("Event updated successfully");
        } catch (err) {
          showBasicAlert("Failed to update event");
        }
      } else {
        // Create new event
        const data = {
          signature,
          title: values.title,
          description: values.description,
          start_date: start.format("YYYY-MM-DD HH:mm:ss"),
          end_date: end.format("YYYY-MM-DD HH:mm:ss"),
          is_all_day: allDay,
          event_type: parseInt(categoryId),
          is_public: values.is_public || false,
        };

        try {
          await api.post("activityCalendarService/createEvent", data);
          onEventSaved?.();
          setIsFormVisible(false);
          setSelectedDate(null);
          setFormPosition(null);
          message.success("Event created successfully");
        } catch (err) {
          showBasicAlert("Failed to create event");
        }
      }
    });
  };

  const handleCancelEvent = () => {
    setIsFormVisible(false);
    setSelectedDate(null);
    setFormPosition(null);
    setIsEditing(false);
    setSelectedEvent(null);
  };

  // Handle edit event - open the form card with event data
  const handleEditEvent = () => {
    if (selectedEvent) {
      // Close detail card and open form card in the same position
      setIsEventDetailVisible(false);
      setIsFormVisible(true);
      setIsEditing(true);

      // Use the same position as the detail card
      if (detailPosition) {
        setFormPosition(detailPosition);
      }

      // Pre-fill the form with event data
      form.setFieldsValue({
        title: selectedEvent.title,
        description: selectedEvent.description,
        start: selectedEvent.start,
        end: selectedEvent.end,
        allDay: selectedEvent.allDay,
        category: selectedEvent.category,
        color: selectedEvent.color,
        is_public: selectedEvent.is_public || false,
      });
    }
  };

  // Handle delete event
  const handleDeleteEvent = async () => {
    if (!selectedEvent?.id) return;

    try {
      await api.post("activityCalendarService/deleteEvent", {
        signature,
        id: selectedEvent.id,
      });
      message.success("Event deleted successfully");
      setIsEventDetailVisible(false);
      setSelectedEvent(null);
      onEventSaved?.();
    } catch (err) {
      message.error("Failed to delete event");
    }
  };

  const handleCloseDetail = () => {
    setIsEventDetailVisible(false);
    setSelectedEvent(null);
  };

  // Handle popover visibility change
  const handlePopoverVisibleChange = (visible: boolean, popoverId: string) => {
    if (visible) {
      setActivePopover(popoverId);
    } else {
      setActivePopover(null);
    }
  };

  const dayNames = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  const isAllDay = Form.useWatch("allDay", form);

  const formatEventTime = (event: Event): string => {
    if (event.allDay) return "All day";
    return `${event.start.format("h:mm A")} - ${event.end.format("h:mm A")}`;
  };

  // Fixed: Better event display with proper styling
  const getEventDisplay = (event: Event) => {
    if (event.allDay) {
      return (
        <div className="all-day-event-pill">
          <div
            className="event-color-dot"
            style={{ backgroundColor: event.color }}
          />
          <span className="event-title-text">{event.title}</span>
        </div>
      );
    }

    return (
      <div className="timed-event-item">
        <div
          className="event-color-bar"
          style={{ backgroundColor: event.color }}
        />
        <div className="event-content">
          <span className="event-title-text">{event.title}</span>
        </div>
      </div>
    );
  };

  // Popover content for remaining events
  const renderRemainingEventsPopover = (day: Dayjs) => {
    const dayEvents = getEventsForDay(day);
    const popoverId = `day-${day.format("YYYY-MM-DD")}`;

    return (
      <div className="remaining-events-popover">
        <div className="popover-title">All Events on {day.format("MMM D")}</div>
        <div className="popover-events-list">
          {dayEvents.map((event) => (
            <div
              key={event.id}
              className="popover-event-item"
              onClick={(e) => {
                e.stopPropagation();
                handleEventClick(event, e as any);
              }}
            >
              <div
                className="popover-event-color"
                style={{ backgroundColor: event.color }}
              />
              <div className="popover-event-content">
                <div className="popover-event-title">{event.title}</div>
                <div className="popover-event-time">
                  {event.allDay
                    ? "All day"
                    : `${event.start.format("h:mm A")} - ${event.end.format(
                        "h:mm A"
                      )}`}
                </div>
                {event.description && (
                  <div className="popover-event-description">
                    {event.description}
                  </div>
                )}
                {event.is_public && (
                  <div className="popover-event-public">🌐 Public Event</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="modern-month-view" style={{ position: "relative" }}>
      {/* Month Header */}
      <div className="month-view-header">
        <div className="month-title">{currentDate.format("MMMM YYYY")}</div>
      </div>

      {/* Day Names Header */}
      <Row className="modern-month-header">
        {dayNames.map((dayName) => (
          // <Col key={dayName} span={3} className="modern-day-header">
          <Col key={dayName} flex="1" className="modern-day-header">
            <div className="day-name">{dayName}</div>
          </Col>
        ))}
      </Row>

      {/* Calendar Grid */}
      <div className="month-grid-container">
        {weeks.map((week, weekIndex) => (
          <Row key={weekIndex} className="modern-month-week">
            {week.map((day) => {
              const latestEvents = getLatestEventsForDay(day);
              const remainingCount = getRemainingEventsCount(day);
              const isCurrentMonth = day.month() === currentDate.month();
              const isToday = day.isSame(dayjs(), "day");
              const isWeekend = day.day() === 0 || day.day() === 6;
              const popoverId = `day-${day.format("YYYY-MM-DD")}`;

              return (
                <Col
                  key={day.format("YYYY-MM-DD")}
                  // span={3}
                  flex="1"
                  className={`modern-month-day ${
                    !isCurrentMonth ? "other-month" : ""
                  } ${isWeekend ? "weekend" : ""}`}
                  onClick={() => onDateClick(day)}
                >
                  <div className="day-cell">
                    {/* Date Number */}
                    <div
                      className={`modern-day-number ${isToday ? "today" : ""} ${
                        !isCurrentMonth ? "faded" : ""
                      }`}
                    >
                      <div className="date-circle">{day.format("D")}</div>
                      {isToday && <div className="today-glow" />}
                    </div>

                    {/* Events - Show only latest one */}
                    <div className="modern-day-events">
                      {latestEvents.map((event, index) => (
                        <Tooltip
                          key={event.id}
                          title={
                            <div className="event-tooltip">
                              <div className="tooltip-title">
                                <div
                                  className="tooltip-color"
                                  style={{ backgroundColor: event.color }}
                                />
                                <strong>{event.title}</strong>
                              </div>
                              <div className="tooltip-time">
                                {event.allDay
                                  ? "All day"
                                  : `${event.start.format(
                                      "h:mm A"
                                    )} - ${event.end.format("h:mm A")}`}
                              </div>
                              {event.description && (
                                <div className="tooltip-description">
                                  {event.description}
                                </div>
                              )}
                              {event.is_public && (
                                <div className="tooltip-public">
                                  🌐 Public Event
                                </div>
                              )}
                            </div>
                          }
                          placement="top"
                        >
                          <div
                            className="event-display"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEventClick(event, e);
                            }}
                          >
                            {getEventDisplay(event)}
                          </div>
                        </Tooltip>
                      ))}

                      {/* More events indicator with popover */}
                      {remainingCount > 0 && (
                        <Popover
                          content={renderRemainingEventsPopover(day)}
                          trigger="click"
                          placement="bottom"
                          overlayClassName="remaining-events-popover-overlay"
                          open={activePopover === popoverId}
                          onOpenChange={(visible) =>
                            handlePopoverVisibleChange(visible, popoverId)
                          }
                        >
                          <div
                            className="more-events-indicator"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Tag color="default" className="more-tag">
                              +{remainingCount} more
                            </Tag>
                          </div>
                        </Popover>
                      )}
                    </div>

                    {/* Add event button for empty days */}
                    {isCurrentMonth && latestEvents.length === 0 && (
                      <div
                        className="add-event-hint"
                        onClick={(e) => handleAddEventClick(day, e)}
                      >
                        <span className="add-icon">+</span>
                      </div>
                    )}
                  </div>
                </Col>
              );
            })}
          </Row>
        ))}
      </div>

      {/* Legend */}
      <div className="month-legend">
        <div className="legend-title">Quick View:</div>
        <div className="legend-items">
          <div className="legend-item">
            <div className="today-sample">
              <div className="date-circle">{dayjs().format("D")}</div>
            </div>
            <span>Today</span>
          </div>
          <div className="legend-item">
            <div className="all-day-sample">
              <div
                className="event-color-dot"
                style={{ backgroundColor: "#1890ff" }}
              />
              <span>All Day Event</span>
            </div>
          </div>
          <div className="legend-item">
            <div className="timed-sample">
              <div
                className="event-color-bar"
                style={{ backgroundColor: "#52c41a" }}
              />
              <span>Timed Event</span>
            </div>
          </div>
          <div className="legend-item">
            <div className="public-sample">
              <span className="public-icon">🌐</span>
              <span>Public Event</span>
            </div>
          </div>
        </div>
      </div>

      {/* Event Form Card */}
      {isFormVisible && formPosition && (
        <Draggable
          handle=".card-header"
          bounds=".modern-month-view"
          defaultPosition={formPosition}
        >
          <Card
            className="event-form-card month-form-card"
            style={{
              width: 420,
              zIndex: 1000,
              position: "absolute",
              top: 0,
              left: 0,
            }}
            styles={{
              body: { padding: 0 },
            }}
          >
            {/* Custom Header */}
            <div className="card-header">
              <div className="card-header-content">
                <div className="card-title-section">
                  <div className="card-icon">📅</div>
                  <div>
                    <div className="card-title">
                      {isEditing ? "Edit Event" : "New Event"}
                    </div>
                    <div className="card-subtitle">
                      {selectedDate
                        ? selectedDate.format("dddd, MMM D, YYYY")
                        : currentDate.format("MMMM YYYY")}
                    </div>
                  </div>
                </div>
                <Button
                  type="text"
                  onClick={handleCancelEvent}
                  className="close-btn"
                  icon={<span>×</span>}
                />
              </div>
            </div>

            {/* Form Content */}
            <div className="card-body">
              <Form form={form} layout="vertical" className="event-form">
                {/* Title Section */}
                <div className="form-section">
                  <div className="section-label">
                    <span className="section-icon">✏️</span>
                    Event Title
                  </div>
                  <Form.Item
                    name="title"
                    rules={[
                      { required: true, message: "Please enter event title" },
                    ]}
                  >
                    <Input
                      placeholder="What's happening?"
                      className="modern-input"
                      size="large"
                    />
                  </Form.Item>
                </div>

                {/* Description Section */}
                <div className="form-section">
                  <div className="section-label">
                    <span className="section-icon">📝</span>
                    Description
                  </div>
                  <Form.Item name="description">
                    <TextArea
                      rows={2}
                      placeholder="Add some details about your event..."
                      className="modern-textarea"
                    />
                  </Form.Item>
                </div>

                {/* Settings Row */}
                <div className="settings-row">
                  <div className="form-section compact">
                    <div className="section-label">All Day</div>
                    <Form.Item name="allDay" valuePropName="checked">
                      <Switch
                        onChange={handleAllDayChange}
                        className="modern-switch"
                        checkedChildren="ON"
                        unCheckedChildren="OFF"
                      />
                    </Form.Item>
                  </div>

                  <div className="form-section compact">
                    <div className="section-label">Category</div>
                    <Form.Item name="category">
                      <Select
                        className="modern-select"
                        placeholder="Select category"
                      >
                        {categories
                          .filter(
                            (cat) =>
                              cat.name !== "Birthday" &&
                              cat.name !== "Anniversary" &&
                              cat.name !== "Holiday"
                          )
                          .map((cat) => (
                            <Option key={cat.id} value={cat.name}>
                              <div className="category-option">
                                <div
                                  className="category-color"
                                  style={{ backgroundColor: cat.color }}
                                />
                                <span>{cat.name}</span>
                              </div>
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </div>
                  <div className="form-section compact">
                    <div className="section-label">Public</div>
                    <Form.Item name="is_public" valuePropName="checked">
                      <Switch
                        className="modern-switch"
                        checkedChildren="Yes"
                        unCheckedChildren="No"
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* Date & Time Section */}
                <div className="form-section">
                  <div className="section-label">
                    <span className="section-icon">⏰</span>
                    Date & Time
                  </div>
                  <div className="datetime-grid">
                    <Form.Item
                      name="start"
                      label="Start"
                      rules={[
                        { required: true, message: "Please select start time" },
                      ]}
                      className="datetime-item"
                    >
                      <DatePicker
                        showTime={
                          !isAllDay
                            ? {
                                format: "h:mm A",
                                use12Hours: true,
                                hourStep: 1,
                                minuteStep: 15,
                                showNow: false,
                              }
                            : false
                        }
                        format={isAllDay ? "MMM D, YYYY" : "MMM D, YYYY h:mm A"}
                        className="modern-datepicker"
                        disabled={isAllDay}
                        disabledTime={isAllDay ? () => ({}) : undefined}
                        placeholder="Start"
                        suffixIcon={<span>📅</span>}
                      />
                    </Form.Item>

                    <Form.Item
                      name="end"
                      label="End"
                      rules={[
                        { required: true, message: "Please select end time" },
                      ]}
                      className="datetime-item"
                    >
                      <DatePicker
                        showTime={
                          !isAllDay
                            ? {
                                format: "h:mm A",
                                use12Hours: true,
                                hourStep: 1,
                                minuteStep: 15,
                                showNow: false,
                              }
                            : false
                        }
                        format={isAllDay ? "MMM D, YYYY" : "MMM D, YYYY h:mm A"}
                        className="modern-datepicker"
                        disabled={isAllDay}
                        disabledTime={isAllDay ? () => ({}) : undefined}
                        placeholder="End"
                        suffixIcon={<span>📅</span>}
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="action-buttons">
                  <Button
                    onClick={handleCancelEvent}
                    className="cancel-btn"
                    size="large"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleSaveEvent}
                    className="save-btn"
                    size="large"
                    icon={<span>💫</span>}
                  >
                    {isEditing ? "Update Event" : "Create Event"}
                  </Button>
                </div>
              </Form>
            </div>
          </Card>
        </Draggable>
      )}

      {/* Event Detail Card */}
      {isEventDetailVisible && selectedEvent && detailPosition && (
        <Draggable
          handle=".card-header"
          bounds=".modern-month-view"
          defaultPosition={detailPosition}
        >
          <Card
            className="event-detail-card"
            style={{
              width: 400,
              zIndex: 1001,
              position: "absolute",
              top: 0,
              left: 0,
            }}
            styles={{
              body: { padding: 0 },
            }}
          >
            {/* Custom Header */}
            <div
              className="card-header detail-header"
              style={{
                background: `linear-gradient(135deg, ${selectedEvent.color}20 0%, ${selectedEvent.color}40 100%)`,
                borderLeft: `4px solid ${selectedEvent.color}`,
              }}
            >
              <div className="card-header-content">
                <div className="card-title-section">
                  <div
                    className="card-icon"
                    style={{ backgroundColor: selectedEvent.color }}
                  >
                    <CalendarOutlined
                      style={{ color: "white", fontSize: "16px" }}
                    />
                  </div>
                  <div>
                    <div className="card-title">{selectedEvent.title}</div>
                    <div className="card-subtitle">
                      {formatEventTime(selectedEvent)}
                    </div>
                  </div>
                </div>
                <Button
                  type="text"
                  onClick={handleCloseDetail}
                  className="close-btn"
                  icon={<CloseOutlined />}
                />
              </div>
            </div>

            {/* Detail Content */}
            <div className="card-body">
              <div className="event-details">
                {/* Event Information */}
                <div className="detail-section">
                  <div className="detail-item">
                    <div className="detail-label">
                      <ClockCircleOutlined className="detail-icon" />
                      Time
                    </div>
                    <div className="detail-value">
                      {selectedEvent.allDay ? (
                        "All Day"
                      ) : (
                        <>
                          {selectedEvent.start.format(
                            "ddd, MMM D, YYYY h:mm A"
                          )}
                          <br />
                          to{" "}
                          {selectedEvent.end.format("ddd, MMM D, YYYY h:mm A")}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="detail-item">
                    <div className="detail-label">
                      <TagOutlined className="detail-icon" />
                      Category
                    </div>
                    <div className="detail-value">
                      <Tag
                        color={selectedEvent.color}
                        style={{ border: `1px solid ${selectedEvent.color}` }}
                      >
                        {selectedEvent.category}
                      </Tag>
                    </div>
                  </div>

                  {selectedEvent.location && (
                    <div className="detail-item">
                      <div className="detail-label">
                        <EnvironmentOutlined className="detail-icon" />
                        Location
                      </div>
                      <div className="detail-value">
                        {selectedEvent.location}
                      </div>
                    </div>
                  )}

                  <div className="detail-item">
                    <div className="detail-label">
                      <FileTextOutlined className="detail-icon" />
                      Visibility
                    </div>
                    <div className="detail-value">
                      <Tag color={selectedEvent.is_public ? "blue" : "default"}>
                        {selectedEvent.is_public
                          ? "Public Event"
                          : "Private Event"}
                      </Tag>
                    </div>
                  </div>
                </div>

                {/* Description */}
                {selectedEvent.description && (
                  <>
                    <Divider style={{ margin: "16px 0" }} />
                    <div className="detail-section">
                      <div className="detail-item">
                        <div className="detail-label">
                          <FileTextOutlined className="detail-icon" />
                          Description
                        </div>
                        <div className="detail-value description-text">
                          {selectedEvent.description}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Action Buttons */}
                <Divider style={{ margin: "16px 0" }} />
                <div className="detail-actions">
                  <Space
                    style={{ width: "100%", justifyContent: "space-between" }}
                  >
                    <Button
                      type="primary"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={handleDeleteEvent}
                      size="middle"
                      disabled={selectedEvent?.is_generated}
                    >
                      Delete
                    </Button>
                    <Button
                      type="primary"
                      icon={<EditOutlined />}
                      onClick={handleEditEvent}
                      size="middle"
                      disabled={selectedEvent?.is_generated}
                    >
                      Edit Event
                    </Button>
                  </Space>
                </div>
              </div>
            </div>
          </Card>
        </Draggable>
      )}
    </div>
  );
};

export default MonthView;
